import React, { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { format } from 'date-fns'

interface InfrastructureObject {
  id: number
  name: string
  address?: string
  location?: string
  street_address?: string
  infrastructure_type: string
}

interface ReadingTypeLabels {
  [key: string]: string
}

interface AddReadingModalProps {
  isOpen: boolean
  onClose: () => void
  token: string
  onReadingAdded: () => void
}

export default function AddReadingModal({ isOpen, onClose, token, onReadingAdded }: AddReadingModalProps) {
  const [objects, setObjects] = useState<InfrastructureObject[]>([])
  const [readingTypes, setReadingTypes] = useState<ReadingTypeLabels>({})
  const [selectedObjectId, setSelectedObjectId] = useState<number | null>(null)
  const [selectedReadingType, setSelectedReadingType] = useState('')
  const [value, setValue] = useState('')
  const [readingDate, setReadingDate] = useState(() => format(new Date(), "yyyy-MM-dd"))
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(false)
  const [isBatchMode, setIsBatchMode] = useState(false)
  const [isCSVMode, setIsCSVMode] = useState(false)
  const [batchReadings, setBatchReadings] = useState<Array<{date: string, value: string, notes: string}>>([
    { date: format(new Date(), "yyyy-MM-dd"), value: '', notes: '' }
  ])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const { showSuccess, showError } = useNotificationContext()

  useEffect(() => {
    if (isOpen) {
      loadData()
    }
  }, [isOpen])

  const loadData = async () => {
    setLoadingData(true)
    try {
      // Load reading types
      const typesResponse = await fetch('/api/statistics/reading-types', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (typesResponse.ok) {
        const typesData = await typesResponse.json()
        setReadingTypes(typesData.labels || {})
        // Store reading types mapping as well
        // Note: We need to fetch from API to get the structure, but for now we'll handle it in the component
      }

      // Load infrastructure objects (SUW and hydroforownie)
      const objectsResponse = await fetch('/api/infrastructure/', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (objectsResponse.ok) {
        const objectsData = await objectsResponse.json()
        // Filter only objects that support readings
        const filteredObjects = objectsData.filter((obj: InfrastructureObject) =>
          obj.infrastructure_type === 'Stacja Uzdatniania wody' ||
          obj.infrastructure_type === 'Hydroforownia'
        )
        setObjects(filteredObjects)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      showError('Błąd', 'Nie udało się załadować danych')
    } finally {
      setLoadingData(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    // Don't execute if not in single mode
    if (isBatchMode || isCSVMode) return

    e.preventDefault()

    if (!selectedObjectId || !selectedReadingType) {
      showError('Błąd walidacji', 'Wybierz obiekt infrastruktury i typ odczytu')
      return
    }

    if (!value.trim() || !readingDate) {
      showError('Błąd walidacji', 'Wartość i data są wymagane')
      return
    }

    const numValue = parseFloat(value)
    if (isNaN(numValue) || numValue < 0) {
      showError('Błąd walidacji', 'Wartość musi być liczba dodatnią')
      return
    }

    setLoading(true)
    try {
      const response = await fetch('/api/statistics/readings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          infrastructure_object_id: selectedObjectId,
          reading_type: selectedReadingType,
          value: numValue,
          reading_date: readingDate,
          notes: notes.trim() || null,
        }),
      })

      if (response.ok) {
        showSuccess('Odczyt został pomyślnie dodany')
        onReadingAdded()
        handleClose()
      } else {
        const errorData = await response.json()
        showError('Błąd', errorData.detail || 'Nie udało dodać odczytu')
      }
    } catch (error) {
      console.error('Error adding reading:', error)
      showError('Błąd', 'Nie udało się dodać odczytu')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedObjectId(null)
    setSelectedReadingType('')
    setValue('')
    setReadingDate(format(new Date(), "yyyy-MM-dd"))
    setNotes('')
    setIsBatchMode(false)
    setIsCSVMode(false)
    setCsvFile(null)
    setBatchReadings([
      { date: format(new Date(), "yyyy-MM-dd"), value: '', notes: '' }
    ])
    onClose()
  }

  const addBatchRow = () => {
    setBatchReadings(prev => [...prev, {
      date: format(new Date(), "yyyy-MM-dd"),
      value: '',
      notes: ''
    }])
  }

  const removeBatchRow = (index: number) => {
    if (batchReadings.length > 1) {
      setBatchReadings(prev => prev.filter((_, i) => i !== index))
    }
  }

  const updateBatchRow = (index: number, field: string, value: string) => {
    setBatchReadings(prev => prev.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    ))
  }

  const handleCSVFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCsvFile(file)
    }
  }

  const handleCSVSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    console.log('CSV Submit - selectedObjectId:', selectedObjectId, typeof selectedObjectId)
    console.log('CSV Submit - selectedReadingType:', selectedReadingType)
    console.log('CSV Submit - csvFile:', csvFile)

    if (!selectedObjectId) {
      showError('Błąd walidacji', 'Wybierz obiekt infrastruktury')
      return
    }

    if (!selectedReadingType) {
      showError('Błąd walidacji', 'Wybierz typ odczytu')
      return
    }

    if (!csvFile) {
      showError('Błąd walidacji', 'Wybierz plik CSV do przesłania')
      return
    }

    setLoading(true)
    try {
      // Create FormData for file upload
      const formData = new FormData()
      formData.append('csv_file', csvFile)
      formData.append('infrastructure_object_id', selectedObjectId.toString())
      formData.append('reading_type', selectedReadingType)

      console.log('Sending CSV request with objectId:', selectedObjectId, 'type:', selectedReadingType)

      const response = await fetch('/api/statistics/csv-import', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (response.ok) {
        const result = await response.json()
        const message = result.message || `Zaimportowano ${result.imported_count} odczytów`
        showSuccess(message)
        onReadingAdded()
        handleClose()
      } else {
        const errorData = await response.json()
        showError('Błąd importu', errorData.detail || 'Nie udało się zaimportować danych z pliku CSV')
      }
    } catch (error) {
      console.error('Error importing CSV:', error)
      showError('Błąd', 'Nie udało się zaimportować danych z pliku CSV')
    } finally {
      setLoading(false)
    }
  }

  const handleBatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedObjectId || !selectedReadingType) {
      showError('Błąd walidacji', 'Wybierz obiekt i typ odczytu')
      return
    }

    // Validate all batch readings
    const invalidRows = batchReadings.filter(row =>
      !row.date || !row.value.trim() ||
      isNaN(parseFloat(row.value)) || parseFloat(row.value) < 0
    )

    if (invalidRows.length > 0) {
      showError('Błąd walidacji', 'Wszystkie pola muszą być poprawnie wypełnione')
      return
    }

    setLoading(true)
    try {
      const readingsToSend = batchReadings.map(row => ({
        infrastructure_object_id: selectedObjectId,
        reading_type: selectedReadingType,
        value: parseFloat(row.value),
        reading_date: row.date,
        notes: row.notes.trim() || null,
      }))

      const response = await fetch('/api/statistics/readings/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          readings: readingsToSend
        }),
      })

      if (response.ok) {
        const result = await response.json()
        showSuccess(`Pomyślnie dodano ${result.added_count} odczytów`)
        onReadingAdded()
        handleClose()
      } else {
        const errorData = await response.json()
        showError('Błąd', errorData.detail || 'Nie udało się dodać odczytów')
      }
    } catch (error) {
      console.error('Error adding batch readings:', error)
      showError('Błąd', 'Nie udało się dodać odczytów')
    } finally {
      setLoading(false)
    }
  }

  const selectedObject = objects.find(obj => obj.id === selectedObjectId)

  // Hardcoded mapping for reading types per infrastructure type
  const readingTypeMapping: { [key: string]: string[] } = {
    'Stacja Uzdatniania wody': ['water_to_network', 'well_water', 'waste_water'],
    'Hydroforownia': ['buffer_input', 'network_output']
  }

  const availableReadingTypes = selectedObject ? (readingTypeMapping[selectedObject.infrastructure_type] || []) : []

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Dodaj odczyt
          </h3>

          {/* Mode selection */}
          <div className="mb-4 space-y-3">
            <div className="p-3 bg-blue-50 rounded-md">
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="inputMode"
                    checked={!isBatchMode && !isCSVMode}
                    onChange={() => {
                      setIsBatchMode(false)
                      setIsCSVMode(false)
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Pojedynczy odczyt
                  </span>
                </label>
                <p className="text-xs text-gray-600 ml-5">
                  Wprowadź jedną wartość dla wybranej daty
                </p>
              </div>
            </div>

            <div className="p-3 bg-green-50 rounded-md">
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="inputMode"
                    checked={isBatchMode && !isCSVMode}
                    onChange={() => {
                      setIsBatchMode(true)
                      setIsCSVMode(false)
                    }}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Odczyty seryjne (ręczne wprowadzanie)
                  </span>
                </label>
                <p className="text-xs text-gray-600 ml-5">
                  Wypełnij tabelę z datami i wartościami do dodania jednocześnie
                </p>
              </div>
            </div>

            <div className="p-3 bg-purple-50 rounded-md">
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="inputMode"
                    checked={isCSVMode}
                    onChange={() => {
                      setIsCSVMode(true)
                      setIsBatchMode(false)
                    }}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Import z pliku CSV
                  </span>
                </label>
                <p className="text-xs text-gray-600 ml-5">
                  Prześlij plik CSV z kolumnami: wartość,data
                </p>
              </div>
            </div>
          </div>

          {loadingData ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2">Ładowanie danych...</span>
            </div>
          ) : (
            <form onSubmit={isCSVMode ? handleCSVSubmit : (isBatchMode ? handleBatchSubmit : handleSubmit)} className="space-y-4">
              {/* Object selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Obiekt infrastruktury *
                </label>
                <select
                  value={selectedObjectId || ''}
                  onChange={(e) => {
                    setSelectedObjectId(e.target.value ? parseInt(e.target.value) : null)
                    setSelectedReadingType('') // Reset reading type when object changes
                  }}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— wybierz obiekt —</option>
                  {objects
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(obj => (
                    <option key={obj.id} value={obj.id}>
                      {obj.name} ({obj.infrastructure_type})
                    </option>
                  ))}
                </select>
                {selectedObject && (
                  <div className="mt-1 text-sm text-gray-600">
                    {selectedObject.location && `${selectedObject.location}`}
                    {selectedObject.street_address && ` • ${selectedObject.street_address}`}
                  </div>
                )}
              </div>

              {/* Reading type selection */}
              {selectedObject && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Typ odczytu *
                  </label>
                  <select
                    value={selectedReadingType}
                    onChange={(e) => setSelectedReadingType(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— wybierz typ —</option>
                    {availableReadingTypes.map(type => (
                      <option key={type} value={type}>
                        {readingTypes[type] || type}
                      </option>
                    ))}
                  </select>
                  {selectedReadingType && (
                    <div className="mt-1 text-sm text-gray-500">
                      Jednostka: m³ (metry sześcienne)
                    </div>
                  )}
                </div>
              )}

              {/* CSV file upload */}
              {isCSVMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plik CSV *
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVFileChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  {csvFile && (
                    <div className="mt-1 text-sm text-gray-600">
                      Wybrany plik: {csvFile.name} ({csvFile.size} bajtów)
                    </div>
                  )}
                  <div className="mt-1 text-sm text-gray-500">
                    Plik CSV powinien zawierać kolumny: wartość,data<br/>
                    Przykład: 123.45,2025-10-01<br/>
                    Separator dziesiętny: kropka (.) lub przecinek (,)
                  </div>
                </div>
              )}

              {isBatchMode ? (
                /* Batch readings table */
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Odczyty seryjne *
                    </label>
                    <button
                      type="button"
                      onClick={addBatchRow}
                      className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                    >
                      + Dodaj wiersz
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Wartość [m³]</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notatki</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Akcja</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {batchReadings.map((row, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2">
                              <input
                                type="date"
                                value={row.date}
                                onChange={(e) => updateBatchRow(index, 'date', e.target.value)}
                                required
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={row.value}
                                onChange={(e) => updateBatchRow(index, 'value', e.target.value)}
                                placeholder="np. 123.45"
                                required
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.notes}
                                onChange={(e) => updateBatchRow(index, 'notes', e.target.value)}
                                placeholder="Opcjonalnie..."
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => removeBatchRow(index)}
                                disabled={batchReadings.length <= 1}
                                className="text-red-600 hover:text-red-800 disabled:text-gray-400"
                                title="Usuń wiersz"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    Wszystkie odczyty będą zarejestrowane o godzinie 10:00
                  </div>
                </div>
              ) : (
                !isCSVMode && (
                <>
                  {/* Single reading inputs */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Wartość [m³] *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      required={!isCSVMode}
                      placeholder="np. 123.45"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data odczytu *
                    </label>
                    <input
                      type="date"
                      value={readingDate}
                      onChange={(e) => setReadingDate(e.target.value)}
                      required={!isCSVMode}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="mt-1 text-sm text-gray-500">
                      Odczyt będzie zawsze rejestrowany o godzinie 10:00
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notatki (opcjonalne)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="Opcjonalne uwagi dotyczące odczytu..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
                )
              )}

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 flex items-center"
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  )}
                  {isCSVMode ? 'Importuj z CSV' : (isBatchMode ? 'Dodaj odczyty seryjne' : 'Dodaj odczyt')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
